import { useGetAsset } from '@/hooks/api/assets/useGetAsset';
import { useGetCriticalGeometryArtifacts } from '@/hooks/api/assets/useGetCriticalGeometryArtifacts';
import { useSearchFilters } from '@/hooks/useSearchFilters';
import { useThemeMode } from '@/hooks/useThemeMode';
import { useToast } from '@/hooks/useToast';
import { api, getBaseUrl } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { getAssetName } from '@/utils/assets';
import { downloadFromSource } from '@/utils/downloadUtils';
import { Box, Button, CircularProgress, Link, Typography, styled } from '@mui/material';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

const MainContentArea = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'row',
  height: '100%',
  overflow: 'hidden',
});

const ArtifactPanel = styled(Box)(({ theme }) => ({
  width: '280px',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.paper,
  borderRight: `1px solid ${theme.palette.divider}`,
  overflowWrap: 'anywhere',
}));

const ArtifactIframe = styled('iframe')({
  flex: 1,
  width: '100%',
  height: '100%',
  border: 0,
});

interface ViewerSession {
  token: string;
  apiLocation: string;
}

const useViewerSession = () => {
  const [session, setSession] = useState<ViewerSession>();
  const [error, setError] = useState<string>();
  useEffect(() => {
    api.viewer
      .viewerToken()
      .then(({ data: { token } }) => setSession({ token, apiLocation: getBaseUrl() }))
      .catch(() => setError('Error fetching viewer'));
  }, []);
  return { session, error };
};

export const CriticalGeometryArtifactViewer = ({ assetId, artifactId }: { assetId: string; artifactId: string }) => {
  const { selectedTenantId: tenantId } = useSearchFilters();
  const { resolvedMode } = useThemeMode();
  const { getPath } = useTenants();
  const { asset } = useGetAsset({ assetId, assetTenantId: tenantId });
  const { criticalGeometryArtifacts, isLoading } = useGetCriticalGeometryArtifacts({
    assetId,
    assetTenantId: tenantId,
  });
  const { session, error } = useViewerSession();
  const { showToast } = useToast();
  const entry = criticalGeometryArtifacts.find(candidate => candidate.artifactId === artifactId);

  const artifactQuery = `assetId=${assetId}&artifactId=${artifactId}`;
  const fileUrl =
    session && `${session.apiLocation}/tenants/${tenantId}/viewer/critical-geometry-artifact-file?${artifactQuery}`;
  const iframeSource =
    session &&
    `${session.apiLocation}/tenants/${tenantId}/viewer/critical-geometry-artifact?token=${session.token}&${artifactQuery}&theme=${resolvedMode}&parentOrigin=${encodeURIComponent(globalThis.location.origin)}`;

  const download = async () => {
    if (!session || !fileUrl || !entry) return;
    try {
      const response = await fetch(fileUrl, { headers: { Authorization: `Bearer ${session.token}` } });
      await downloadFromSource(response, { filename: entry.name });
    } catch {
      showToast({ message: 'Failed to download critical geometry file', type: 'error' });
    }
  };

  if (isLoading || !session) {
    return error ? <Box>{error}</Box> : <CircularProgress />;
  }

  return (
    <MainContentArea>
      <ArtifactPanel>
        <Typography variant='h6'>{entry?.name ?? 'Critical geometry file'}</Typography>
        {asset && (
          <Link component={RouterLink} to={getPath(`asset/${assetId}`)}>
            {getAssetName(asset)}
          </Link>
        )}
        {entry && (
          <Typography variant='body2' color='text.secondary'>
            {new Date(entry.createdAt).toLocaleString()}
          </Typography>
        )}
        <Button variant='outlined' onClick={() => void download()} disabled={!entry}>
          Download
        </Button>
      </ArtifactPanel>
      <ArtifactIframe
        key={artifactId}
        src={iframeSource}
        title='Critical Geometry File Viewer'
        sandbox='allow-same-origin allow-scripts allow-popups allow-forms allow-popups-to-escape-sandbox'
        referrerPolicy='no-referrer'
      />
    </MainContentArea>
  );
};
